import React, { useEffect, useRef, useState } from 'react';

const StandardHeroVideo = ({ scrollProgress }) => {
    const [isVideoReady, setIsVideoReady] = useState(false);
    const containerRef = useRef(null);
    const playerRef = useRef(null);
    const [isMuted, setIsMuted] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const toggleMute = () => {
        if (playerRef.current) {
            if (isMuted) {
                playerRef.current.unMute();
            } else {
                playerRef.current.mute();
            }
            setIsMuted(!isMuted);
        }
    };

    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    useEffect(() => {
        if (!window.YT) {
            const tag = document.createElement('script');
            tag.src = 'https://www.youtube.com/iframe_api';
            tag.async = true;
            const firstScriptTag = document.getElementsByTagName('script')[0];
            firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        }

        let player = null;

        const initPlayer = () => {
            if (!containerRef.current) return;

            containerRef.current.innerHTML = '<div id="hero-yt-player"></div>';

            player = new window.YT.Player('hero-yt-player', {
                videoId: 'LPj1lf3laQc',
                width: '100%',
                height: '100%',
                playerVars: {
                    autoplay: 1,
                    mute: 1,
                    controls: 0,
                    loop: 1,
                    playlist: 'LPj1lf3laQc',
                    rel: 0,
                    modestbranding: 1,
                    showinfo: 0,
                    iv_load_policy: 3,
                    fs: 0,
                    disablekb: 1,
                    playsinline: 1,
                },
                events: {
                    onReady: (event) => {
                        event.target.playVideo();
                        const qualities = event.target.getAvailableQualityLevels();
                        if (qualities.includes('hd1080')) {
                            event.target.setPlaybackQuality('hd1080');
                        } else if (qualities.includes('hd720')) {
                            event.target.setPlaybackQuality('hd720');
                        }
                        setIsVideoReady(true);
                        playerRef.current = event.target;
                    },
                    onStateChange: (event) => {
                        if (event.data === window.YT.PlayerState.ENDED) {
                            player.playVideo();
                        }
                        if (event.data === window.YT.PlayerState.PLAYING) {
                            setTimeout(() => {
                                const currentQuality = event.target.getPlaybackQuality();
                                const availableQualities = event.target.getAvailableQualityLevels();
                                if (currentQuality !== 'hd1080' && availableQualities.includes('hd1080')) {
                                    event.target.setPlaybackQuality('hd1080');
                                }
                            }, 1000);
                        }
                    }
                },
            });
        };

        if (window.YT && window.YT.Player) {
            initPlayer();
        } else {
            window.onYouTubeIframeAPIReady = () => {
                initPlayer();
            };
        }

        return () => {
            if (player && player.destroy) {
                player.destroy();
            }
        };
    }, []);

    const desktopStyle = {
        position: 'absolute',
        top: '40%',
        left: '50%',
        width: 'max(120vh, 67.5vw)',
        height: 'max(213.33vh, 120vw)',
        transform: 'translate(-50%, -50%) rotate(-90deg) scale(0.9)',
        pointerEvents: 'none'
    };

    const mobileStyle = {
        position: 'absolute',
        top: '50%',
        left: '54%',
        width: '75vw',
        height: '100vw',
        transform: 'translate(-50%, -50%) rotate(-90deg) scale(1.30)',
        pointerEvents: 'none'
    };

    return (
        <>
            <div
                style={isFullscreen ? {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    zIndex: 9999,
                    background: 'black',
                    opacity: 1,
                    transition: 'all 0.5s ease',
                } : {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 0,
                    overflow: 'hidden',
                    opacity: Math.max(0, 1 - scrollProgress * 3),
                    transition: 'opacity 0.1s ease',
                }}
            >
                <div
                    ref={containerRef}
                    style={isFullscreen ? {
                        position: 'absolute',
                        top: '0',
                        left: '0',
                        width: '100%',
                        height: '100%',
                        transform: 'none',
                        pointerEvents: 'none'
                    } : (isMobile ? mobileStyle : desktopStyle)}
                >
                    {/* API will inject iframe here */}
                </div>

                {!isVideoReady && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: '#0a3f26',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2
                    }}>
                    </div>
                )}
            </div>

            {/* Video Controls - Always visible outside scroll opacity */}
            {isVideoReady && (
                <div style={{
                    position: isFullscreen ? 'fixed' : 'absolute',
                    bottom: isFullscreen ? 'auto' : (isMobile ? '25px' : '96px'),
                    top: isFullscreen ? '30px' : 'auto',
                    right: '30px',
                    zIndex: isFullscreen ? 10000 : 100,
                    display: 'flex',
                    gap: '12px',
                    pointerEvents: 'auto',
                    opacity: isMobile ? (isFullscreen ? 1 : Math.max(0, 1 - scrollProgress * 3)) : 1,
                    transition: 'opacity 0.3s ease'
                }}>
                    <button
                        onClick={toggleMute}
                        style={{
                            background: 'rgba(0, 0, 0, 0.6)',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            borderRadius: '50%',
                            width: '40px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            cursor: 'pointer',
                            backdropFilter: 'blur(10px)',
                            transition: 'all 0.3s ease',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(212, 165, 117, 0.8)';
                            e.currentTarget.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)';
                            e.currentTarget.style.transform = 'scale(1)';
                        }}
                    >
                        {isMuted ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 5L6 9H2v6h4l5 4V5z"></path>
                                <line x1="23" y1="9" x2="17" y2="15"></line>
                                <line x1="17" y1="9" x2="23" y2="15"></line>
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 5L6 9H2v6h4l5 4V5z"></path>
                                <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                            </svg>
                        )}
                    </button>

                    {/* Fullscreen button - only visible on mobile */}
                    {isMobile && (
                        <button
                            onClick={toggleFullscreen}
                            style={{
                                background: 'rgba(0, 0, 0, 0.6)',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                borderRadius: '50%',
                                width: '40px',
                                height: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                cursor: 'pointer',
                                backdropFilter: 'blur(10px)',
                                transition: 'all 0.3s ease',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(212, 165, 117, 0.8)';
                                e.currentTarget.style.transform = 'scale(1.1)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)';
                                e.currentTarget.style.transform = 'scale(1)';
                            }}
                        >
                            {isFullscreen ? (
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M15 3h6v6"></path>
                                    <path d="M9 21H3v-6"></path>
                                    <path d="M21 3l-7 7"></path>
                                    <path d="M3 21l7-7"></path>
                                </svg>
                            )}
                        </button>
                    )}
                </div>
            )}
        </>
    );
};

export default StandardHeroVideo;
